/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import { Grid, Paper, Select, Space, Switch, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAtom, IconMicroscope, IconNotes } from '@tabler/icons-react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../../../../../components/BasicValueUnits';
import EntityAddressPicker from '../../../../../../../../components/core/EntityAddressPicker/EntityAddressPicker';
import EntityPicker from '../../../../../../../../components/core/EntityPicker/EntityPicker';
import PageSection from '../../../../../../../../components/core/PageSection/PageSection';
import ResiduePicker from '../../../../../../../../components/core/ResiduePicker/ResiduePicker';
import { ProposalGeneralData } from '../../../../../../../../contexts/core/proposals/ProposalGeneral.context';
import { useProposalResidueContext } from '../../../../../../../../contexts/core/proposals/ProposalResidue.context';
import {
  EntityTypeType,
  ResidueStateOfMatterType,
  UnitOfMeasureType,
} from '../../../../../../../../models/core/cache.type';
import { Action } from '../../../../../../../../models/core/core.type';
import {
  EntityAddressType,
  EntitySearchResponseType,
} from '../../../../../../../../models/core/entities.type';
import { ProposalResidueType } from '../../../../../../../../models/core/proposals.type';
import { ResidueType } from '../../../../../../../../models/core/residues.type';
import { Feature } from '../../../../../../../../utils/constants.utils';
import { buildFakeAuditObject, newGuid } from '../../../../../../../../utils/helper.utils';

type FormViewData = {
  action: Action;
  id: string;
  idResiduo: number | null;
  residuoData: ResidueType | null;
  residuo: string;
  codigoResiduoEstadoFisico: string | null;
  residuoCliente: string;
  residuoClientePerigoso: boolean;
  minimoAceitavel: number | null;
  minimoAceitavelIdUnidadeMedida1: string | null;
  minimoAceitavelIdUnidadeMedida2: string | null;
  estimativa: number | null;
  estimativaIdUnidadeMedida1: string | null;
  estimativaIdUnidadeMedida2: string | null;
  faturamentoDireto: boolean;
  compra: boolean;
  cobrarTolerancia: boolean;
  clienteData: EntitySearchResponseType | null;
  idCliente: number | null;
  cliente: string;
  enderecoData: EntityAddressType | null;
  idEntidadeEndereco: number | null;
  endereco: string;
  tecnologiaAtual: string | null;
  observacao: string | null;
};

type ProposalFormViewProps = {
  referenceData: {
    unitOfMeasures: UnitOfMeasureType[];
    entityTypeData: EntityTypeType[];
    residueStateOfMatterType: ResidueStateOfMatterType[];
  };
  proposalGeneralData: ProposalGeneralData;
  origItem: ProposalResidueType | null;
  item: ProposalResidueType | null;
  idProposta: number | undefined;
  disabled: boolean;
  callback(item: ProposalResidueType | null, action: string, confirmed: boolean): void;
  zIndex: number;
};

const ResidueGeneralFormView = forwardRef((props: ProposalFormViewProps, ref) => {
  const { proposalResidueData, setProposalResidueData } = useProposalResidueContext();
  const refMinimoAceitavel = useRef<BasicValueUnitsRef>(null);
  const refEstimativa = useRef<BasicValueUnitsRef>(null);

  const quantityUnitOptions = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        x.tipo === 'Massa' || x.tipo === 'Volume' || (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const minimumFrequencyUnitOptions = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && (x.unidadeMedidaSigla === 'per' || x.unidadeMedidaSigla === 'col'))
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const estimateFrequencyUnitOptions = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const buildResidueDataFromItem = (item: ProposalResidueType | null): ResidueType | null => {
    if (!item) {
      return null;
    }

    return {
      ...buildFakeAuditObject(),
      idResiduo: Number(item.idResiduo),
      residuo: item.residuo,
      perigoso: item.perigoso,
    };
  };

  const buildClienteDataFromItem = (item: ProposalResidueType | null): EntitySearchResponseType | null => {
    if (!item) {
      return null;
    }

    return {
      ...buildFakeAuditObject(),
      idEntidade: Number(item.idCliente),
      tipos: [],
      cnpj: item.clienteCNPJ || null,
      razaoSocial: item.clienteRazaoSocial || null,
      nomeFantasia: item.clienteNomeFantasia || null,
      incricaoEstadual: null,
      incricaoMunicipal: null,
      cpf: item.clienteCPF || null,
      nome: item.clienteNome || null,
      webSite: null,
      aceitaEncontroContas: false,
    };
  };

  const buildEnderecoDataFromItem = (item: ProposalResidueType | null): EntityAddressType | null => {
    if (!item) {
      return null;
    }

    return {
      ...buildFakeAuditObject(),
      idEntidadeEndereco: item.idEntidadeEndereco,
      idEntidade: item.idCliente,
      codigoEnderecoTipo: '',
      enderecoTipo: '',
      codigoEstado: item.codigoEstado,
      estado: '',
      cidade: item.cidade,
      cidadeCodigoIBGE: item.cidadeCodigoIBGE || null,
      bairro: item.bairro,
      logradouro: item.logradouro,
      numero: item.numero,
      complemento: item.complemento || null,
      referencia: item.referencia || null,
      cep: item.cep,
      latitude: item.latitude || null,
      longitude: item.longitude || null,
      contatos: [],
    };
  };

  const form = useForm<FormViewData>({
    initialValues: {
      action: props.origItem ? Action.Nothing : Action.Add,
      id: props.item?.id || newGuid(),
      residuoData: buildResidueDataFromItem(props.item),
      idResiduo: props.item?.idResiduo || null,
      residuo: props.item?.residuo || '',
      codigoResiduoEstadoFisico: props.item?.codigoResiduoEstadoFisico?.toString() || null,
      residuoCliente: props.item?.residuoCliente || '',
      residuoClientePerigoso: props.item?.residuoClientePerigoso || false,
      minimoAceitavel: props.item?.minimoAceitavel || null,
      minimoAceitavelIdUnidadeMedida1: props.item?.minimoAceitavelIdUnidadeMedida1?.toString() || null,
      minimoAceitavelIdUnidadeMedida2: props.item?.minimoAceitavelIdUnidadeMedida2?.toString() || null,
      estimativa: props.item?.estimativa ?? null,
      estimativaIdUnidadeMedida1: props.item?.estimativaIdUnidadeMedida1?.toString() || null,
      estimativaIdUnidadeMedida2: props.item?.estimativaIdUnidadeMedida2?.toString() || null,
      faturamentoDireto: props.item?.faturamentoDireto || false,
      compra: props.item?.compra || false,
      cobrarTolerancia: props.item?.cobrarTolerancia || false,
      clienteData: buildClienteDataFromItem(props.item) || props.proposalGeneralData.cliente,
      idCliente: props.item?.idCliente || props.proposalGeneralData.cliente?.idEntidade || null,
      cliente:
        props.item?.clienteNomeFantasia ||
        props.item?.clienteRazaoSocial ||
        props.item?.clienteNome ||
        props.proposalGeneralData.cliente?.nomeFantasia ||
        props.proposalGeneralData.cliente?.razaoSocial ||
        props.proposalGeneralData.cliente?.nome ||
        '',
      enderecoData: buildEnderecoDataFromItem(props.item),
      idEntidadeEndereco: props.item?.idEntidadeEndereco || null,
      endereco: props.item?.idEntidadeEndereco
        ? `${props.item.logradouro}, ${props.item.numero} | ${props.item.bairro} - ${props.item.cidade}/${props.item.codigoEstado}`
        : '',
      tecnologiaAtual: props.item?.tecnologiaAtual || '',
      observacao: props.item?.observacao || '',
    },
    validate: {
      idResiduo: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
      residuoCliente: (value) => (value.trim() === '' ? 'Campo obrigatório' : null),
      codigoResiduoEstadoFisico: (value: string | null) => (!value ? 'Campo obrigatório' : null),
      idCliente: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
      idEntidadeEndereco: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
    },
  });

  const isModified = (
    minimoAceitavelData?: BasicValueUnitsData,
    estimativaData?: BasicValueUnitsData
  ): boolean => {
    const origData = `${props.origItem?.idResiduo || ''}
    |${props.origItem?.codigoResiduoEstadoFisico || ''}
    |${props.origItem?.residuoCliente || ''}
    |${props.origItem?.residuoClientePerigoso || ''}
    |${Number(props.origItem?.minimoAceitavel) || ''}
    |${props.origItem?.minimoAceitavelIdUnidadeMedida1 || ''}
    |${props.origItem?.minimoAceitavelIdUnidadeMedida2 || ''}
    |${Number(props.origItem?.estimativa) || ''}
    |${props.origItem?.estimativaIdUnidadeMedida1 || ''}
    |${props.origItem?.estimativaIdUnidadeMedida2 || ''}
    |${props.origItem?.faturamentoDireto || ''}
    |${props.origItem?.compra || ''}
    |${props.origItem?.cobrarTolerancia || ''}
    |${props.origItem?.idCliente || ''}
    |${props.origItem?.idEntidadeEndereco || ''}
    |${props.origItem?.tecnologiaAtual || ''}
    |${props.origItem?.observacao || ''}`;

    const formData = `${form.values.idResiduo || ''}
    |${form.values.codigoResiduoEstadoFisico || ''}
    |${form.values.residuoCliente || ''}
    |${form.values.residuoClientePerigoso || ''}
    |${Number(minimoAceitavelData?.valor ?? form.values.minimoAceitavel) || ''}
    |${minimoAceitavelData?.idUnidade1 ?? (form.values.minimoAceitavelIdUnidadeMedida1 || '')}
    |${minimoAceitavelData?.idUnidade2 ?? (form.values.minimoAceitavelIdUnidadeMedida2 || '')}
    |${Number(estimativaData?.valor ?? form.values.estimativa) || ''}
    |${estimativaData?.idUnidade1 ?? (form.values.estimativaIdUnidadeMedida1 || '')}
    |${estimativaData?.idUnidade2 ?? (form.values.estimativaIdUnidadeMedida2 || '')}
    |${form.values.faturamentoDireto || ''}
    |${form.values.compra || ''}
    |${form.values.cobrarTolerancia || ''}
    |${form.values.idCliente || ''}
    |${form.values.idEntidadeEndereco || ''}
    |${form.values.tecnologiaAtual || ''}
    |${form.values.observacao || ''}`;

    return origData !== formData;
  };

  useImperativeHandle(ref, () => ({
    validate(): ProposalResidueType | null {
      const minimoAceitavelData = refMinimoAceitavel.current?.validate();
      const estimativaData = refEstimativa.current?.validate();

      if (form.validate().hasErrors || !estimativaData || minimoAceitavelData === null) {
        throw Error('Existem pendências a serem corrigidas.');
      }

      let formValues = form.values;

      if (props.origItem) {
        if (isModified(minimoAceitavelData, estimativaData)) {
          formValues.action = Action.Modify;
        } else {
          formValues = {
            ...formValues,
            ...props.origItem,
            action: Action.Nothing,
            minimoAceitavelIdUnidadeMedida1: minimoAceitavelData?.idUnidade1 || '',
            minimoAceitavelIdUnidadeMedida2: minimoAceitavelData?.idUnidade2 || '',
            estimativaIdUnidadeMedida1: estimativaData.idUnidade1 || '',
            estimativaIdUnidadeMedida2: estimativaData.idUnidade2 || '',
          };
        }
      }

      const formItem: ProposalResidueType = {
        ...formValues,
        ...buildFakeAuditObject(),
        idResiduo: Number(formValues.idResiduo),
        residuo: formValues.residuo,
        idCliente: Number(formValues.idCliente),
        clienteCNPJ: formValues.clienteData?.cnpj,
        clienteRazaoSocial: formValues.clienteData?.razaoSocial,
        clienteNomeFantasia: formValues.clienteData?.nomeFantasia,
        clienteCPF: formValues.clienteData?.cpf,
        clienteNome: formValues.clienteData?.nome,
        idEntidadeEndereco: Number(formValues.idEntidadeEndereco),
        codigoEstado: formValues.enderecoData?.codigoEstado,
        cidade: formValues.enderecoData?.cidade,
        cidadeCodigoIBGE: formValues.enderecoData?.cidadeCodigoIBGE,
        bairro: formValues.enderecoData?.bairro,
        logradouro: formValues.enderecoData?.logradouro,
        numero: formValues.enderecoData?.numero,
        complemento: formValues.enderecoData?.complemento,
        referencia: formValues.enderecoData?.referencia,
        cep: formValues.enderecoData?.cep,
        latitude: formValues.enderecoData?.latitude,
        longitude: formValues.enderecoData?.longitude,
        idProposta: props.idProposta || -1,
        minimoAceitavel:
          minimoAceitavelData?.valor && minimoAceitavelData.valor > 0 ? minimoAceitavelData.valor : null,
        minimoAceitavelIdUnidadeMedida1: Number(minimoAceitavelData?.idUnidade1) || null,
        minimoAceitavelIdUnidadeMedida2: Number(minimoAceitavelData?.idUnidade2) || null,
        estimativa: estimativaData?.valor && estimativaData.valor > 0 ? estimativaData.valor : null,
        estimativaIdUnidadeMedida1: estimativaData.idUnidade1 ? Number(estimativaData.idUnidade1) : null,
        estimativaIdUnidadeMedida2: estimativaData.idUnidade2 ? Number(estimativaData.idUnidade2) : null,
      };

      formItem.minimoAceitavelUnidadeMedida1 =
        props.referenceData.unitOfMeasures.find(
          (x) => x.idUnidadeMedida === formItem.minimoAceitavelIdUnidadeMedida1
        )?.unidadeMedida || '';

      formItem.minimoAceitavelUnidadeMedida2 =
        props.referenceData.unitOfMeasures.find(
          (x) => x.idUnidadeMedida === formItem.minimoAceitavelIdUnidadeMedida2
        )?.unidadeMedida || '';

      formItem.estimativaUnidadeMedida1 =
        props.referenceData.unitOfMeasures.find(
          (x) => x.idUnidadeMedida === formItem.estimativaIdUnidadeMedida1
        )?.unidadeMedida || '';

      formItem.estimativaUnidadeMedida2 =
        props.referenceData.unitOfMeasures.find(
          (x) => x.idUnidadeMedida === formItem.estimativaIdUnidadeMedida2
        )?.unidadeMedida || '';

      formItem.tecnologiaAtual = formItem.tecnologiaAtual?.trim() || null;
      formItem.observacao = formItem.observacao?.trim() || null;

      return formItem;
    },
    getFormItemValue(item: keyof FormViewData) {
      return form.getInputProps(item).value;
    },
    clear() {
      form.reset();
      form.setFieldValue('idCliente', -1);
      form.setFieldValue('cliente', '');
      form.setFieldValue('idEntidadeEndereco', -1);
      form.setFieldValue('endereco', '');
    },
  }));

  useEffect(() => {
    setProposalResidueData({
      residuo: form.values.idResiduo ? { ...form.values, ...buildFakeAuditObject(), descricao: null } : null,
      codigoResiduoEstadoFisico: form.values.codigoResiduoEstadoFisico,
      residuoCliente: form.values.residuoCliente,
      residuoClientePerigoso: form.values.residuoClientePerigoso,
      minimoAceitavel: form.values.minimoAceitavel,
      minimoAceitavelIdUnidadeMedida1: form.values.minimoAceitavelIdUnidadeMedida1,
      minimoAceitavelIdUnidadeMedida2: form.values.minimoAceitavelIdUnidadeMedida2,
      estimativa: form.values.estimativa,
      estimativaIdUnidadeMedida1: form.values.estimativaIdUnidadeMedida1,
      estimativaIdUnidadeMedida2: form.values.estimativaIdUnidadeMedida2,
      idEntidadeEndereco: form.values.idEntidadeEndereco,
      planos: props.item?.planos || [],
    });
  }, [form.values, props.item?.planos, setProposalResidueData]);

  return (
    <div>
      <form id="proposal-residue" noValidate>
        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Geral"
            text="Informações gerais do resíduo como referências, dados do cliente e local."
          />
          <Space h="xs" />

          <Grid columns={5}>
            <Grid.Col span={4}>
              <ResiduePicker
                label="Resíduo"
                formItem={form.getInputProps('idResiduo')}
                idsToBeDisabled={[]}
                disabled={props.disabled}
                callback={(item: ResidueType | null) => {
                  if (item) {
                    form.setFieldValue('residuoData', item);
                    form.setFieldValue('idResiduo', item.idResiduo);
                    form.setFieldValue('residuo', item.residuo);
                    setProposalResidueData({ ...proposalResidueData, residuo: item });
                  }
                }}
                zIndex={props.zIndex + 1}
                useCache
              />
            </Grid.Col>
            {/* <Grid.Col span={1}>
              <div style={{ marginTop: -5 }}>
                <PageViewProperty
                  label="Perigoso?"
                  text={form.values.residuoData?.perigoso ? 'Sim' : 'Não'}
                  size="sm"
                />
              </div>
            </Grid.Col> */}
            <Grid.Col span={1}>
              <Select
                leftSection={<IconMicroscope size={15} />}
                label="Estado Físico"
                placeholder="Selecione..."
                data={props.referenceData.residueStateOfMatterType.map((x) => ({
                  value: x.codigoResiduoEstadoFisico,
                  label: x.residuoEstadoFisico,
                }))}
                searchable
                required
                {...form.getInputProps('codigoResiduoEstadoFisico')}
              />
            </Grid.Col>
          </Grid>
          <Space h="xs" />

          <Grid columns={5}>
            <Grid.Col span={3}>
              <TextInput
                leftSection={<IconAtom size={15} />}
                label="Resíduo (Cliente)"
                placeholder="Resíduo"
                maxLength={100}
                required
                disabled={props.disabled}
                {...form.getInputProps('residuoCliente')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <div style={{ marginTop: 30 }}>
                <Switch
                  label="Resíduo Perigoso (Cliente)?"
                  {...form.getInputProps('residuoClientePerigoso', { type: 'checkbox' })}
                />
              </div>
            </Grid.Col>
          </Grid>
          <Space h="xs" />

          <Grid columns={2}>
            <Grid.Col span={1}>
              <Textarea
                leftSection={<IconNotes size={15} />}
                label="Tecnologia atual"
                placeholder="Observação sobre a tecnologia atual utilizada pelo cliente"
                autosize
                maxLength={500}
                {...form.getInputProps('tecnologiaAtual')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Textarea
                leftSection={<IconNotes size={15} />}
                label="Observação"
                placeholder="Observação sobre o resíduo"
                autosize
                maxLength={500}
                {...form.getInputProps('observacao')}
              />
            </Grid.Col>
          </Grid>
        </Paper>
        <Space h="lg" />

        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Condições Comerciais"
            text="Informações comerciais gerais do resíduo e regras operacionais."
          />
          <Space h="xs" />

          <Grid columns={4}>
            <Grid.Col span={2}>
              <BasicValueUnits
                ref={refMinimoAceitavel}
                isEdit
                disabled={props.disabled}
                zIndex={props.zIndex + 1}
                label="Mínimo Aceitável"
                placeholderValor="Mínimo Aceitável"
                validationMode="all-or-none"
                valueMustBePositiveWhenFilled
                value={{
                  valor: form.values.minimoAceitavel,
                  idUnidade1: form.values.minimoAceitavelIdUnidadeMedida1,
                  idUnidade2: form.values.minimoAceitavelIdUnidadeMedida2,
                }}
                unit1Options={quantityUnitOptions}
                unit2Options={minimumFrequencyUnitOptions}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <BasicValueUnits
                ref={refEstimativa}
                isEdit
                disabled={props.disabled}
                zIndex={props.zIndex + 1}
                label="Estimativa"
                placeholderValor="Estimativa"
                validationMode="all-or-none"
                valueMustBePositiveWhenFilled
                value={{
                  valor: form.values.estimativa,
                  idUnidade1: form.values.estimativaIdUnidadeMedida1,
                  idUnidade2: form.values.estimativaIdUnidadeMedida2,
                }}
                unit1Options={quantityUnitOptions}
                unit2Options={estimateFrequencyUnitOptions}
              />
            </Grid.Col>
          </Grid>
          <Space h="xs" />

          <Grid columns={4}>
            <Grid.Col span={1}>
              <div>
                <Switch
                  label="Faturamento Direto?"
                  {...form.getInputProps('faturamentoDireto', { type: 'checkbox' })}
                />
              </div>
            </Grid.Col>
            <Grid.Col span={1}>
              <div>
                <Switch label="Compra?" {...form.getInputProps('compra', { type: 'checkbox' })} />
              </div>
            </Grid.Col>
            <Grid.Col span={1}>
              <div>
                <Switch
                  label="Cobrar Tolerâncias?"
                  {...form.getInputProps('cobrarTolerancia', { type: 'checkbox' })}
                />
              </div>
            </Grid.Col>
          </Grid>
        </Paper>
        <Space h="lg" />

        <Paper shadow="xs" p="md" withBorder>
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Cliente e Local"
            text="Cliente e local onde a prestação de serviço deverá ocorrer."
          />
          <Space h="xs" />
          <Grid columns={4}>
            <Grid.Col span={2}>
              <EntityPicker
                label="Cliente"
                description={null}
                formItem={form.getInputProps('idCliente') || undefined}
                idsToBeDisabled={[]}
                disabled={props.disabled}
                required
                callback={(item: EntitySearchResponseType | EntitySearchResponseType[] | null) => {
                  if (!Array.isArray(item) && item) {
                    form.setFieldValue('clienteData', item);
                    form.setFieldValue('idCliente', item.idEntidade);
                    form.setFieldValue('cliente', item.nomeFantasia || item.razaoSocial || item.nome || '');
                    form.setFieldValue('idEntidadeEndereco', -1);
                    form.setFieldValue('endereco', '');
                  }
                }}
                zIndex={props.zIndex + 1}
                useCache
                allowMultiple={false}
                query={undefined}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <EntityAddressPicker
                key={form.values.idCliente!}
                label="Endereço"
                idEntidade={form.values.idCliente!}
                formItem={form.getInputProps('idEntidadeEndereco')}
                idsToBeDisabled={[]}
                callback={async (item: EntityAddressType | null) => {
                  if (item) {
                    form.setFieldValue('enderecoData', item);
                    form.setFieldValue('idEntidadeEndereco', item.idEntidadeEndereco!);
                    form.setFieldValue(
                      'endereco',
                      `${item.logradouro}, ${item.numero} | ${item.bairro} - ${item.cidade}/${item.codigoEstado}`
                    );
                  }
                }}
                disabled={props.disabled || !form.values.idCliente}
                required
                zIndex={props.zIndex + 1}
              />
            </Grid.Col>
          </Grid>
        </Paper>
      </form>
    </div>
  );
});

export default ResidueGeneralFormView;
